// src/pages/ClientSupplierInvoicesList.js
import React from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import API_URL from "../api/baseURL";
import { Page, Panel, Badge, Table, Tr, Td, EmptyState, Loading, ErrorNote } from "../components/ui/Iparc";
import { fmt } from "../theme/iparc";

function useInvoices(token) {
    const [state, setState] = React.useState({ loading: true, error: "", data: [] });

    React.useEffect(() => {
        if (!token) return undefined;
        let alive = true;
        (async () => {
            setState({ loading: true, error: "", data: [] });
            try {
                // The endpoint is behind authenticateUser and this call sent no
                // header, so it 401'd and the page only ever showed its error
                // state. There is no global axios auth default in this app.
                const res = await axios.get(`${API_URL}/quotations/client/supplier-invoices`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const arr = Array.isArray(res.data)
                    ? res.data
                    : Array.isArray(res.data?.items)
                        ? res.data.items
                        : [];
                if (alive) setState({ loading: false, error: "", data: arr });
            } catch (e) {
                if (!alive) return;
                const msg = e?.response?.data?.error || e.message || "Échec du chargement des factures";
                setState({ loading: false, error: msg, data: [] });
            }
        })();
        return () => {
            alive = false;
        };
    }, [token]);

    return state;
}

function fmtDate(d) {
    try {
        return d ? new Date(d).toLocaleDateString("fr-FR") : "—";
    } catch {
        return d || "—";
    }
}

// Supplier-invoice statuses -> label + signal tone.
const STATUS = {
    awaiting_payment: { label: "À payer", tone: "warn" },
    paid: { label: "Payée", tone: "ok" },
    cancelled: { label: "Annulée", tone: "danger" },
};
const statusOf = (s) => STATUS[s] || { label: String(s || "—").replace(/_/g, " "), tone: "neutral" };

export default function ClientSupplierInvoicesList() {
    const { token } = useAuth();
    const { loading, error, data } = useInvoices(token);
    const navigate = useNavigate();

    const rows = Array.isArray(data) ? data : [];
    const due = rows
        .filter((r) => r?.status !== "paid")
        .reduce((s, r) => s + Number(r?.totals?.totalPublic || 0), 0);

    return (
        <Page
            title="Factures fournisseurs"
            subtitle="Finances"
            actions={
                due > 0 && (
                    <div className="text-right">
                        <p className="text-[10.5px] uppercase tracking-[.16em] text-[#6E665C]">Reste à payer</p>
                        <p className="font-['Space_Grotesk',sans-serif] text-[20px] font-semibold tabular-nums text-[#F4F1EA] leading-tight">
                            {fmt(due)} <span className="text-[12px] text-[#8F877B]">F</span>
                        </p>
                    </div>
                )
            }
        >
            {error && <ErrorNote>{error}</ErrorNote>}

            <Panel title="Factures reçues" bodyClass="">
                {loading ? (
                    <Loading label="Chargement des factures…" />
                ) : rows.length === 0 ? (
                    <EmptyState
                        title="Aucune facture de fournisseur"
                        hint="Les factures émises par vos fournisseurs de pièces apparaîtront ici."
                    />
                ) : (
                    <Table head={["Date", "Statut", "Fournisseur", "Véhicule", "Montant"]} minWidth={760}>
                        {rows.map((r, idx) => {
                            const id = r?._id || r?.id || String(idx);
                            const supplier = r?.sellerSupplier || {};
                            const veh = r?.vehicle || {};
                            const st = statusOf(r?.status || "awaiting_payment");
                            const vehicleLabel = [veh?.brand, veh?.model].filter(Boolean).join(" ");
                            return (
                                <Tr key={id} onClick={() => navigate(`/owner/invoices/${id}`)}>
                                    <Td>{fmtDate(r?.createdAt)}</Td>
                                    <Td><Badge tone={st.tone}>{st.label}</Badge></Td>
                                    <Td className="text-[#F4F1EA]">
                                        {supplier?.businessName || supplier?.name || "—"}
                                    </Td>
                                    <Td>
                                        {vehicleLabel || "—"}
                                        {veh?.plate && (
                                            <span className="ml-1.5 font-['Space_Grotesk',sans-serif] text-[12px] text-[#F4F1EA]">
                                                {veh.plate}
                                            </span>
                                        )}
                                    </Td>
                                    <Td mono className="text-right">
                                        {fmt(r?.totals?.totalPublic || 0)}{" "}
                                        <span className="text-[10.5px] text-[#6E665C]">F</span>
                                    </Td>
                                </Tr>
                            );
                        })}
                    </Table>
                )}
            </Panel>
        </Page>
    );
}
